/**
 * @fileoverview Flagged Transactions — master review table
 * @module jobs/phases/review/flaggedTransactions/MasterReviewTable
 *
 * One row per managed analysis agent. The Status column is decision-aware
 * (Reviewed / In Review / Transactions Identified / No Transactions
 * Identified); Agreed shows the
 * value and count the practitioner has agreed with; To Review shows what is
 * still pending; Agent Status carries the run state (Live / Updating /
 * Locked / Stale / Blocked). Hovering Blocked shows why the agent cannot
 * run. Rows are clickable — no Open button.
 */

import { Badge } from "@/ui/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { GitPullRequestArrow, Layers, Scale } from "lucide-react";
import { cn } from "@/utils/misc";
import {
  agentText,
  fmtMoney,
  outcomeDisplay,
  type WorkspaceHeadUI,
  type FlaggedHeadKey,
} from "./flaggedShared";
import { AgentStatusBadge } from "./AgentStatusBadge";
import { displayProvisionWithDuty } from "../duties";

export function MasterReviewTable(props: {
  heads: WorkspaceHeadUI[];
  onOpenHead: (headKey: FlaggedHeadKey) => void;
}) {
  const { heads, onOpenHead } = props;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Analysed Claims</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[28%]">Reasoning</TableHead>
            <TableHead className="text-right">Est. Recoverable</TableHead>
            <TableHead className="text-right">Agreed</TableHead>
            <TableHead className="text-right">To Review</TableHead>
            <TableHead className="text-right">Confidence</TableHead>
            <TableHead>Agent Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {heads.map((head) => {
            const display = outcomeDisplay(head);
            return (
              <TableRow
                key={head.headKey}
                className="cursor-pointer"
                onClick={() => onOpenHead(head.headKey)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{head.name}</span>
                    {head.proposedSummary && (
                      <span title="The agent has a proposed update awaiting review">
                        <GitPullRequestArrow className="h-3.5 w-3.5 text-violet-500" />
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {displayProvisionWithDuty(
                      "recoverable",
                      head.headKey,
                      head.provision,
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {head.outcome || head.flagCount > 0 ? (
                    <div className="flex flex-col items-start gap-0.5">
                      <Badge color={display.color} variant="soft" size="sm">
                        {display.label}
                      </Badge>
                      {display.detail && (
                        <span className="text-[10px] text-muted-foreground">
                          {display.detail}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {head.headline
                      ? agentText(head.headline)
                      : head.uiDescription}
                  </p>
                </TableCell>
                <TableCell className="text-right">
                  {head.decisions.recoverableValue > 0 ? (
                    <div className="flex flex-col items-end">
                      <span className="font-mono text-sm font-semibold tabular-nums">
                        {fmtMoney(head.decisions.recoverableValue)}
                      </span>
                      {head.overlap && (
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="mt-0.5 flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-400">
                                <Layers className="h-3 w-3" />
                                {head.overlap.heads.length === 1
                                  ? `also under ${head.overlap.heads[0]}`
                                  : `also under ${head.overlap.heads.length} claims`}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              {`${fmtMoney(head.overlap.value)} of this recoverable is also captured under: ${head.overlap.heads.join(
                                ", ",
                              )}. The same dollars may be counted across claims — recover once.`}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {/* Claims with a purpose / fault element: the agent
                          surfaces the transactions, the practitioner assesses
                          the element. */}
                      {head.subjectiveElement &&
                        head.subjectiveElement.undocumentedCount > 0 && (
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="mt-0.5 flex items-center gap-1 text-[10px] text-muted-foreground">
                                  <Scale className="h-3 w-3" />
                                  {head.subjectiveElement.label.toLowerCase()}{" "}
                                  to be assessed
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                {`${head.subjectiveElement.provision} requires ${head.subjectiveElement.label.toLowerCase()}, which the practitioner assesses with regard to the circumstances and evidence. ${head.subjectiveElement.undocumentedCount} transaction${
                                  head.subjectiveElement.undocumentedCount === 1
                                    ? ""
                                    : "s"
                                } totalling ${fmtMoney(head.subjectiveElement.undocumentedValue)} have no documented material on it.`}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {head.decisions.accepted > 0 ? (
                    <div className="flex flex-col items-end">
                      <span className="font-mono text-sm font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
                        {fmtMoney(head.decisions.acceptedValue)}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        {head.decisions.accepted} transaction
                        {head.decisions.accepted === 1 ? "" : "s"}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={cn(
                      "text-sm font-semibold tabular-nums",
                      head.decisions.pending > 0 &&
                        "text-amber-700 dark:text-amber-400",
                    )}
                  >
                    {head.flagCount > 0 ? head.decisions.pending : "—"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {head.confidence !== null
                      ? `${Math.round(head.confidence * 100)}%`
                      : "—"}
                  </span>
                </TableCell>
                <TableCell>
                  <AgentStatusBadge
                    status={head.status}
                    basisNote={head.reviewWindow.basisNote}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
